// src/business/pages/finance/FinanceApprovals.jsx
import { useCallback, useEffect, useState } from "react";
import { CheckCircle, XCircle, DollarSign, Clock } from "lucide-react";
import { financeAPI } from "../../services/businessErpService";
import { PageHeader, StatusBadge, FormModal, BtnSecondary, BtnDanger, EmptyState, Pagination } from "../../components/erp";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { fmtPeso } from "../../../utils/formatters";

const APPROVALS_PAGE_SIZE = 4;

export default function FinanceApprovals() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Reject modal
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeAPI.listPending();
      setPending(res?.data || []);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const totalPages = Math.max(1, Math.ceil(pending.length / APPROVALS_PAGE_SIZE));
  const visiblePending = pending.slice((page - 1) * APPROVALS_PAGE_SIZE, page * APPROVALS_PAGE_SIZE);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const handleApprove = async (poId) => {
    setProcessing(true);
    try {
      await financeAPI.approvePO(poId, { notes: "" });
      fetch();
    } catch (e) {
      alert(e?.message || "Approval failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await financeAPI.rejectPO(rejectTarget, { notes: rejectNotes });
      setRejectTarget(null);
      setRejectNotes("");
      fetch();
    } catch (e) {
      alert(e?.message || "Rejection failed");
    } finally {
      setProcessing(false);
    }
  };

  const totalValue = pending.reduce((sum, po) => sum + (po.unit_price_cents || 0) * (po.quantity || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Finance" title="Finance Approvals" subtitle="Review and approve purchase orders" />

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Pending Approvals" value={loading ? "—" : pending.length} icon={Clock} />
        <StatCard title="Total Value" value={loading ? "—" : fmtPeso(totalValue)} icon={DollarSign} />
        <StatCard title="Awaiting Review" value={loading ? "—" : pending.filter((p) => p.status === "PENDING_FINANCE").length} icon={Clock} accent />
      </div>

      <Panel title="Pending Purchase Orders">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl bg-neutral-100 dark:bg-neutral-800" />
            ))}
          </div>
        ) : pending.length === 0 ? (
          <EmptyState icon={CheckCircle} title="All caught up" description="No purchase orders pending financial review." />
        ) : (
          <div className="space-y-3">
            {visiblePending.map((po) => (
              <div
                key={po.id}
                className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-neutral-700 dark:bg-neutral-800/50"
              >
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-neutral-800 dark:text-neutral-100">PO-{po.id}</span>
                    <StatusBadge status={po.status} />
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-neutral-600 dark:text-neutral-300">
                    <span>Supplier: <strong>{po.supplier_name || po.supplier_company || "—"}</strong></span>
                    <span>Product: <strong>{po.product_name || "—"}</strong></span>
                    <span>Qty: <strong>{po.quantity}</strong></span>
                  </div>
                  <div className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                    Unit: {fmtPeso(po.unit_price_cents)} &middot; Total: {fmtPeso((po.unit_price_cents || 0) * (po.quantity || 0))}
                  </div>
                  {po.notes && <p className="text-xs text-neutral-500 dark:text-neutral-400">{po.notes}</p>}
                </div>

                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleApprove(po.id)}
                    disabled={processing}
                    className="business-primary-action inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 transition disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" /> Approve
                  </button>
                  <button
                    onClick={() => setRejectTarget(po.id)}
                    disabled={processing}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 transition dark:bg-rose-950 dark:text-rose-300 dark:hover:bg-rose-900 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" /> Reject
                  </button>
                </div>
              </div>
            ))}
            <div className="flex flex-col gap-2 border-t border-neutral-200 pt-2 sm:flex-row sm:items-center sm:justify-between dark:border-neutral-800">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Showing {(page - 1) * APPROVALS_PAGE_SIZE + 1}-{Math.min(page * APPROVALS_PAGE_SIZE, pending.length)} of {pending.length}
              </span>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        )}
      </Panel>

      {/* Reject reason modal */}
      <FormModal open={!!rejectTarget} onClose={() => setRejectTarget(null)} title="Reject Purchase Order" size="sm"
        footer={
          <>
            <BtnSecondary onClick={() => setRejectTarget(null)}>Cancel</BtnSecondary>
            <BtnDanger onClick={handleReject} disabled={processing}>
              {processing ? "Rejecting…" : "Reject PO"}
            </BtnDanger>
          </>
        }
      >
        <div>
          <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Rejection Reason</label>
          <textarea
            rows={3} value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
            placeholder="Explain why this PO is being rejected…"
          />
        </div>
      </FormModal>
    </div>
  );
}
